import { HireViewType } from '@core/enums';
import { formatDate } from '@helpers/date';
import DesktopOnly from '@stories/Components/Containers/Responsive/DesktopOnly';
import MobileOnly from '@stories/Components/Containers/Responsive/MobileOnly';
import Quantity from '@stories/Components/Forms/Quantity/Quantity';
import TextArea from '@stories/Components/Forms/TextArea/TextArea';
import Image from '@stories/Components/Media/Image/Image';
import React, { useState } from 'react';
import { HireCardStyles, HireCardStyles as S } from './HireCard.styles';
import { ReactComponent as DeleteIcon } from '@img/icons/delete.svg';
import differenceInDays from 'date-fns/differenceInDays';
import EcommerceService from '@core/ecommerce/services/EcommerceService';
import { useDispatch } from 'react-redux';
import isEqual from 'date-fns/isEqual';
import Button from '@stories/Components/Buttons/Button/Button';
import NewDatePicker from '@stories/Components/Forms/NewDatePicker/DatePicker';
const HireCard = (props) => {
    const [quantity, setQuantity] = useState(props.hire.quantity);
    const [answer, setAnswer] = useState(props.hire.productQuestion?.answerText || '');
    const [disabled, setDisabled] = useState(false);
    const [startDate, setStartDate] = useState(props.hire.startDate);
    const [endDate, setEndDate] = useState(props.hire.endDate);
    const [initialQuantity, setInitialQuantity] = useState(props.hire.quantity);
    const [initialAnswer, setInitialAnswer] = useState(props.hire.productQuestion?.answerText || '');
    const [initialStartDate, setInitialStartDate] = useState(props.hire.startDate);
    const [initialEndDate, setInitialEndDate] = useState(props.hire.endDate);
    const hasChanged = quantity !== initialQuantity ||
        answer !== initialAnswer ||
        !isEqual(initialStartDate, startDate) ||
        !isEqual(initialEndDate, endDate);
    const [error, setError] = useState();
    const dispatch = useDispatch();
    const variantPriceText = `${props.hire.variant}: ${props.hire.priceFormatted} ${props.hire.priceSuffix}`;
    // + 1 to include the starting date
    const hirePeriodText = `${differenceInDays(props.hire.endDate, props.hire.startDate) + 1} ${props.hire.dayHirePeriodText}`;
    const handleDelete = async () => {
        setDisabled(true);
        const result = await EcommerceService.DeleteBasketItem({ productId: props.hire.id });
        dispatch({ type: 'BASKET/SET_BASKET', payload: result.result });
        setDisabled(false);
    };
    const updateBasket = async () => {
        setDisabled(true);
        const response = await EcommerceService.UpdateBasketItem({
            productId: props.hire.id,
            hireDateEnd: EcommerceService.getApiFormattedDate(endDate),
            hireDateStart: EcommerceService.getApiFormattedDate(startDate),
            quantity: quantity,
            productQuestionAnswer: answer,
        });
        if (!response.status.success) {
            if (response.validationErrors && response.validationErrors.length > 0) {
                setError(response.validationErrors[0].message);
                setDisabled(false);
            }
            return;
        }
        if (response.status.success) {
            dispatch({ type: 'BASKET/SET_BASKET', payload: response.result });
            setError(undefined);
            // Update the initial values
            setInitialStartDate(startDate);
            setInitialEndDate(endDate);
            setInitialQuantity(quantity);
            setInitialAnswer(answer);
            setDisabled(false);
        }
    };
    switch (props.hire.hireViewType) {
        default:
        case HireViewType.Editable: {
            return (React.createElement(S.Card, { disabled: disabled },
                React.createElement(S.Media, null,
                    React.createElement(Image, { src: props.hire.imageUrl, width: 168, height: 168, layout: "responsive", objectFit: "cover", alt: props.hire.title || '', draggable: false })),
                React.createElement(S.Content, null,
                    React.createElement(S.Top, null,
                        React.createElement(S.DeleteButton, { onClick: handleDelete },
                            React.createElement(DeleteIcon, null)),
                        React.createElement(S.Title, null, props.hire.title),
                        React.createElement(S.Small, null, variantPriceText),
                        React.createElement(MobileOnly, null,
                            React.createElement(S.SelectQuantity, null,
                                React.createElement(S.Label, null, props.hire.quantityLabel),
                                React.createElement(Quantity, { value: quantity, onChange: setQuantity, min: 0 }))),
                        React.createElement(S.Text, null, hirePeriodText)),
                    React.createElement(S.Bottom, null,
                        React.createElement(S.Split, null,
                            React.createElement(NewDatePicker, { label: `${props.hire.startDateLabel} - ${props.hire.endDateLabel}`, readOnlyInputs: true, leadTimeDays: 0, startDate: startDate, onStartDateChanged: setStartDate, endDate: endDate, onEndDateChanged: setEndDate, excludedDates: props.excludedDates }),
                            React.createElement(DesktopOnly, null,
                                React.createElement(S.SelectQuantity, null,
                                    React.createElement(S.Label, null, props.hire.quantityLabel),
                                    React.createElement(Quantity, { value: quantity, onChange: setQuantity, min: 0 })))),
                        React.createElement(S.ProductQuestions, null, props.hire.productQuestion &&
                            props.hire.productQuestion.questionText.length > 0 ? (React.createElement(TextArea, { id: "productQuestion", required: true, label: props.hire.productQuestion.questionText, placeholder: props.hire.productQuestion.questionText, value: answer, onChange: (e) => setAnswer(e.currentTarget.value), isValid: answer !== undefined })) : null),
                        React.createElement(Button, { branding: "secondary", disabled: !hasChanged, title: "Save Changes", onClick: () => updateBasket() })),
                    error ? React.createElement(HireCardStyles.Error, null, error) : null)));
        }
        case HireViewType.ReadOnly: {
            return (React.createElement(S.Card, { disabled: disabled },
                React.createElement(S.Media, null,
                    React.createElement(Image, { src: props.hire.imageUrl, width: 168, height: 168, layout: "responsive", objectFit: "cover", alt: props.hire.title || '', draggable: false })),
                React.createElement(S.Content, null,
                    React.createElement(S.Top, null,
                        React.createElement(S.Title, null, props.hire.title),
                        React.createElement(S.Field, null,
                            React.createElement(S.Small, null, variantPriceText)),
                        React.createElement(S.Field, null,
                            React.createElement(S.Text, null, hirePeriodText))),
                    React.createElement(S.Bottom, { marginAuto: true },
                        React.createElement(S.Dates, null,
                            React.createElement(S.Field, null,
                                React.createElement(S.Label, null, props.hire.startDateLabel),
                                formatDate(props.hire.startDate)),
                            React.createElement(S.Field, null,
                                React.createElement(S.Label, null, props.hire.endDateLabel),
                                formatDate(props.hire.endDate))),
                        props.hire.productQuestion ? (React.createElement(S.Field, null,
                            React.createElement(S.Label, null, props.hire.productQuestion.questionText),
                            props.hire.productQuestion.answerText)) : null))));
        }
    }
};
export default HireCard;
